/* iter-bulk1 — Bulk Novel Wizard (Premier-only).
 *
 * Two modes:
 *   Build New — up to 30 novel slots configured from scratch.
 *   Import ZIP — drop a .zip of .docx manuscripts; each file is auto-
 *                promoted to a new Project and runs through 8 AI pipeline
 *                stages: import → synopsis → world_builder → plot →
 *                characters → continuity → polish → export.
 *
 * iter-p2-j — Volume raised to 30 novels per run for both modes.
 * MAX_ITEMS stays in sync with `routes_bulk_novel.MAX_ITEMS_PER_RUN`.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Loader2, Rocket, Plus, X, Zap, CheckCircle2, AlertTriangle,
  BookText, PauseCircle, Ban, Upload, FileArchive, ExternalLink,
  Check, RefreshCw, Minus, ChevronDown, ChevronUp,
} from 'lucide-react';
import { zipImportApi } from '@/lib/api';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const DEFAULTS = { chapters_count: 10, words_per_chapter: 2500 };
const MAX_ITEMS = 30;  // iter-p2-j — bumped to 30 novels per run

const emptyItem = () => ({
  title:             '',
  genre:             '',
  chapters_count:    DEFAULTS.chapters_count,
  words_per_chapter: DEFAULTS.words_per_chapter,
  synopsis:          '',
  user_guidance:     '',
  framework_id:      '',   // iter-bulk2
  series_voice_source_project_id: '',   // iter-voice1
});

export default function BulkNovelWizard({ open, onOpenChange, initialFrameworkId = '' }) {
  const [items, setItems]  = useState([emptyItem()]);
  const [submitting, setSubmitting] = useState(false);
  const [runId, setRunId]  = useState(null);
  const [run, setRun]      = useState(null);

  // ── ZIP Import mode ──────────────────────────────────────────────────────
  const [mode, setMode]           = useState('build');  // 'build' | 'import'
  const [zipDragging, setZipDrag] = useState(false);
  const [zipUploading, setZipUp]  = useState(false);
  const [zipRunId, setZipRunId]   = useState(null);
  const [zipRun, setZipRun]       = useState(null);
  const [zipExpanded, setZipExp]  = useState({});      // { idx: bool }
  const fileInputRef              = useRef(null);
  // iter-bulk2 — Frameworks + published books load once the modal opens
  // so each slot picker can render selections without a per-slot roundtrip.
  const [frameworks, setFrameworks] = useState([]);
  const [voiceSources, setVoiceSources] = useState([]);
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/framework`).then((r) => setFrameworks(r.data?.frameworks || [])).catch(() => {});
    axios.get(`${API}/authors/me/bookshelf`).then((r) => {
      const pub = (r.data?.published || []).filter((c) => c.words >= 5000);
      setVoiceSources(pub);
    }).catch(() => {});
  }, [open]);

  // iter-studio-dashboard — Preload the writer's best-performing
  // framework into the first slot when the wizard is opened from the
  // Studio Dashboard's "Draft next batch" CTA. Only runs when the
  // wizard is fresh (single empty slot).
  useEffect(() => {
    if (!open || !initialFrameworkId) return;
    setItems((prev) => {
      if (prev.length !== 1) return prev;
      const [first] = prev;
      if (first.framework_id || first.title || first.synopsis) return prev;
      return [{ ...first, framework_id: initialFrameworkId }];
    });
  }, [open, initialFrameworkId]);

  const reset = useCallback(() => {
    setItems([emptyItem()]);
    setSubmitting(false);
    setRunId(null);
    setRun(null);
    setZipRunId(null);
    setZipRun(null);
    setZipExp({});
    setZipDrag(false);
  }, []);

  // Poll existing bulk-build run
  useEffect(() => {
    if (!runId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/bulk-novel/runs/${runId}`);
        if (cancelled) return;
        setRun(r.data);
      } catch {
        /* transient — keep polling */
      }
    };
    tick();
    const t = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(t); };
  }, [runId]);

  // Poll ZIP import run
  useEffect(() => {
    if (!zipRunId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await zipImportApi.run(zipRunId);
        if (cancelled) return;
        setZipRun(r);
        if (['done', 'done_with_errors', 'cancelled', 'failed'].includes(r.status)) {
          clearInterval(t);
        }
      } catch {/* transient */}
    };
    tick();
    const t = setInterval(tick, 3500);
    return () => { cancelled = true; clearInterval(t); };
  }, [zipRunId]);

  // ── ZIP import handlers ────────────────────────────────────────────────
  const handleZipFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      toast.error('Please drop a .zip file.'); return;
    }
    setZipUp(true);
    try {
      const r = await zipImportApi.start(file);
      setZipRunId(r.run_id);
      toast.success(`Pipeline started for ${r.total_novels} novel(s). Processing in background.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    } finally {
      setZipUp(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setZipDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleZipFile(f);
  };

  const cancelZip = async () => {
    if (!zipRunId) return;
    await zipImportApi.cancel(zipRunId).catch(() => {});
    toast.info('Run cancelled.');
    setZipRunId(null); setZipRun(null);
  };
  const update = (i, patch) => setItems((prev) => {
    const next = [...prev]; next[i] = { ...next[i], ...patch }; return next;
  });
  const addSlot    = () => items.length < MAX_ITEMS && setItems([...items, emptyItem()]);
  const removeSlot = (i) => setItems(items.filter((_, idx) => idx !== i));

  const totalChapters = items.reduce((s, it) => s + Number(it.chapters_count || 0), 0);
  const totalWords    = items.reduce(
    (s, it) => s + Number(it.chapters_count || 0) * Number(it.words_per_chapter || 0), 0);
  const est$          = ((totalChapters * 15000) / 1_000_000) * 10;

  const submit = async () => {
    // Client-side validation — every slot needs a title.
    const problem = items.find((it) => !it.title.trim());
    if (problem) { toast.error('Every novel needs a title.'); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/bulk-novel/queue`, {
        items: items.map((it) => ({
          title:             it.title.trim(),
          genre:             it.genre.trim(),
          chapters_count:    Number(it.chapters_count),
          words_per_chapter: Number(it.words_per_chapter),
          synopsis:          it.synopsis.trim(),
          user_guidance:     it.user_guidance.trim(),
          framework_id:      it.framework_id || null,
          series_voice_source_project_id: it.series_voice_source_project_id || null,
        })),
      });
      setRunId(r.data.run_id);
      toast.success(`Queued ${items.length} novel${items.length === 1 ? '' : 's'} — Claude is drafting now.`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === 'string' ? detail
          : e?.response?.status === 402 ? 'This feature is on Premier Studio — upgrade to unlock.'
          : 'Could not queue novels.',
      );
    } finally { setSubmitting(false); }
  };

  const cancel = async () => {
    if (!runId || !window.confirm('Cancel remaining novels? Chapters already drafted stay.')) return;
    try {
      await axios.post(`${API}/bulk-novel/runs/${runId}/cancel`);
      toast.info('Cancel requested — Claude will stop between chapters.');
    } catch { toast.error('Cancel failed.'); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent
        data-testid="bulk-novel-wizard"
        className="max-h-[90vh] max-w-5xl overflow-hidden bg-white p-0 flex flex-col"
      >
        <DialogHeader className="border-b bg-gradient-to-r from-oxblood-50 to-amber-50 px-5 py-3.5 shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-[16px]"
                         style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <Zap className="h-4 w-4 text-oxblood-600" />
              Volume production — Bulk Novel Studio
              <span className="ml-2 rounded-full bg-oxblood-700 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-white">
                Premier Studio
              </span>
            </DialogTitle>
            {/* Mode switcher */}
            <div className="flex rounded-lg border border-border overflow-hidden text-[12px]">
              <button
                type="button"
                onClick={() => setMode('build')}
                className={`px-3 py-1.5 font-medium transition-colors ${mode === 'build' ? 'bg-oxblood-700 text-white' : 'bg-white text-neutral-600 hover:bg-neutral-50'}`}
                data-testid="bulk-mode-build"
              >Build New</button>
              <button
                type="button"
                onClick={() => setMode('import')}
                className={`flex items-center gap-1.5 px-3 py-1.5 font-medium transition-colors ${mode === 'import' ? 'bg-oxblood-700 text-white' : 'bg-white text-neutral-600 hover:bg-neutral-50'}`}
                data-testid="bulk-mode-import"
              ><FileArchive className="h-3 w-3" /> Import ZIP</button>
            </div>
          </div>
          <p className="text-[11.5px] text-neutral-600">
            A refined production pipeline for authors filling a shelf. Queue up to
            {' '}{MAX_ITEMS}{' '}full-length novels; each is drafted end-to-end by
            Claude against your frameworks, character bibles, and voice profile —
            in parallel, without your attention. Progress updates every four
            seconds. You can close this dialog and return; the studio keeps
            working.
          </p>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {/* ── ZIP IMPORT MODE ──────────────────────────────────────────── */}
          {mode === 'import' ? (
            <ZipImportPanel
              run={zipRun}
              uploading={zipUploading}
              dragging={zipDragging}
              expanded={zipExpanded}
              fileInputRef={fileInputRef}
              onDrop={onDrop}
              onDragOver={(e) => { e.preventDefault(); setZipDrag(true); }}
              onDragLeave={() => setZipDrag(false)}
              onFileChange={(e) => handleZipFile(e.target.files?.[0])}
              onCancel={cancelZip}
              onReset={() => { setZipRunId(null); setZipRun(null); }}
              onToggleExpand={(idx) => setZipExp((p) => ({ ...p, [idx]: !p[idx] }))}
            />
          ) : run ? (
            <RunProgress run={run} onCancel={cancel} onReset={reset} />
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-3">
                {items.map((it, i) => (
                  <SlotCard
                    key={i} idx={i} item={it}
                    canRemove={items.length > 1}
                    frameworks={frameworks}
                    voiceSources={voiceSources}
                    onChange={(patch) => update(i, patch)}
                    onRemove={() => removeSlot(i)}
                  />
                ))}
                {items.length < MAX_ITEMS && (
                  <button
                    onClick={addSlot}
                    className="flex min-h-[280px] flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-neutral-300 p-4 text-neutral-500 transition hover:border-oxblood-400 hover:text-oxblood-600"
                    data-testid="bulk-novel-add-slot"
                  >
                    <Plus className="h-6 w-6" />
                    <span className="text-[12.5px] font-semibold">Add another novel</span>
                    <span className="text-[10.5px]">{items.length} of {MAX_ITEMS}</span>
                  </button>
                )}
              </div>

              {/* Run summary */}
              <div className="mt-5 grid gap-3 rounded-md border border-neutral-200 bg-neutral-50 p-4 sm:grid-cols-4"
                   data-testid="bulk-novel-summary">
                <Kpi label="Novels"        value={items.length} />
                <Kpi label="Total chapters" value={totalChapters} />
                <Kpi label="Total words"   value={totalWords.toLocaleString()} />
                <Kpi label="Est. AI cost"  value={`$${est$.toFixed(2)}`}
                       hint="Rough — meter still enforces your $ ceiling per chapter." />
              </div>

              <div className="mt-4 flex items-center justify-end gap-2">
                <Button variant="ghost" onClick={() => onOpenChange(false)}
                        data-testid="bulk-novel-cancel-form">
                  Cancel
                </Button>
                <Button
                  onClick={submit}
                  disabled={submitting}
                  data-testid="bulk-novel-submit"
                  className="gap-1 bg-oxblood-700 hover:bg-oxblood-800 text-white"
                >
                  {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Rocket className="h-3 w-3" />}
                  Queue {items.length} novel{items.length === 1 ? '' : 's'}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value, hint }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="mt-0.5 font-mono-stat text-[18px] font-semibold text-neutral-900">{value}</div>
      {hint && <div className="text-[10px] text-neutral-500">{hint}</div>}
    </div>
  );
}

function SlotCard({ idx, item, canRemove, onChange, onRemove, frameworks = [], voiceSources = [] }) {
  const selectedFramework = frameworks.find((f) => f.id === item.framework_id);
  // iter-voice2 — Live sample preview. Fetches the exact 5k-char VOICE
  // SAMPLE block Claude will see, so authors trust the pick before
  // spending tokens. Rendered as a collapsible <details> beneath the
  // voice-source picker.
  const [showSample, setShowSample] = React.useState(false);
  const [sampleText, setSampleText] = React.useState('');
  const [sampleLoading, setSampleLoading] = React.useState(false);
  const src = item.series_voice_source_project_id;
  React.useEffect(() => {
    if (!showSample || !src) { setSampleText(''); return; }
    let cancelled = false;
    setSampleLoading(true);
    axios.get(`${API}/projects/${src}/series-voice/samples?max_chars=5000`,
              { params: {} })
      .then((r) => { if (!cancelled) setSampleText(r.data?.text || '(no drafted prose yet on that source)'); })
      .catch(() => { if (!cancelled) setSampleText('Could not load sample.'); })
      .finally(() => { if (!cancelled) setSampleLoading(false); });
    // Note: /samples requires the sample source to be attached to a
    // project. Since we haven't created the target yet, we call the
    // endpoint on the SOURCE project directly — it returns its own
    // opening prose when hit with no `series_voice_source_project_id`
    // set. Backend gracefully returns empty when the source has no
    // designated child, so we fall back to fetching the source's own
    // opening chapters via a second call.
    return () => { cancelled = true; };
  }, [showSample, src]);
  // Fallback fetch — pull raw chapters when /samples returned empty
  // (source not itself attached to another project).
  React.useEffect(() => {
    if (!showSample || !src || sampleText || sampleLoading) return undefined;
    let cancelled = false;
    axios.get(`${API}/projects/${src}/chapters`)
      .then((r) => {
        if (cancelled) return;
        const chs = Array.isArray(r.data) ? r.data : (r.data?.chapters || []);
        const first = chs
          .filter((c) => (c.content || '').trim())
          .sort((a, b) => (a.number || 0) - (b.number || 0))
          .slice(0, 2);
        if (!first.length) { setSampleText('No drafted prose to sample yet.'); return; }
        const built = first.map((c) => `[${c.title || `Ch ${c.number}`}]\n${(c.content || '').slice(0, 2500)}`).join('\n\n');
        setSampleText(built.slice(0, 5000));
      })
      .catch(() => setSampleText('Could not load sample.'));
    return () => { cancelled = true; };
  }, [showSample, src, sampleText, sampleLoading]);
  return (
    <div className="relative flex flex-col gap-2 rounded-md border border-neutral-200 bg-white p-3 shadow-sm"
         data-testid={`bulk-novel-slot-${idx}`}>
      {canRemove && (
        <button
          onClick={onRemove}
          className="absolute right-1.5 top-1.5 rounded-full p-1 text-neutral-400 hover:bg-neutral-100 hover:text-rose-600"
          data-testid={`bulk-novel-slot-remove-${idx}`}
          aria-label="Remove slot"
        >
          <X className="h-3 w-3" />
        </button>
      )}
      <div className="text-[10px] font-bold uppercase tracking-wider text-oxblood-700">Novel {idx + 1}</div>
      <Input
        placeholder="Title"
        value={item.title}
        onChange={(e) => onChange({ title: e.target.value.slice(0, 200) })}
        data-testid={`bulk-novel-title-${idx}`}
        className="h-8 text-[12.5px]"
      />
      <Input
        placeholder="Genre (e.g. dark romance, epic fantasy)"
        value={item.genre}
        onChange={(e) => onChange({ genre: e.target.value.slice(0, 80) })}
        data-testid={`bulk-novel-genre-${idx}`}
        className="h-8 text-[12px]"
      />
      {/* iter-bulk2 — Framework template picker */}
      <label className="text-[10.5px] text-neutral-600">
        <span className="flex items-center gap-1">
          <BookText className="h-3 w-3 text-oxblood-600" />
          Framework template
          <span className="ml-auto text-[9px] font-normal text-neutral-400">
            {selectedFramework ? '25-beat plan applied' : 'freeform draft'}
          </span>
        </span>
        <select
          value={item.framework_id || ''}
          onChange={(e) => onChange({ framework_id: e.target.value })}
          data-testid={`bulk-novel-framework-${idx}`}
          className="mt-0.5 h-8 w-full rounded-sm border border-neutral-300 bg-white px-2 text-[11.5px]"
        >
          <option value="">— No template (use synopsis) —</option>
          {frameworks.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name} {f.chapter_count ? `· ${f.chapter_count} ch` : ''}
            </option>
          ))}
        </select>
        {selectedFramework && (
          <div className="mt-0.5 text-[10px] italic text-neutral-500">
            Overrides chapter count + words/ch from the framework.
          </div>
        )}
      </label>
      {/* iter-voice1 — Series voice source picker */}
      <label className="text-[10.5px] text-neutral-600">
        <span className="flex items-center gap-1">
          <BookText className="h-3 w-3 text-emerald-600" />
          Match voice of…
          <span className="ml-auto text-[9px] font-normal text-neutral-400">
            {voiceSources.length ? 'series voice lock' : 'publish a book first'}
          </span>
        </span>
        <select
          value={item.series_voice_source_project_id || ''}
          onChange={(e) => onChange({ series_voice_source_project_id: e.target.value })}
          data-testid={`bulk-novel-voice-${idx}`}
          disabled={voiceSources.length === 0}
          className="mt-0.5 h-8 w-full rounded-sm border border-neutral-300 bg-white px-2 text-[11.5px] disabled:opacity-50"
        >
          <option value="">— Fresh voice —</option>
          {voiceSources.map((v) => (
            <option key={v.project_id} value={v.project_id}>{v.title}</option>
          ))}
        </select>
        {/* iter-voice2 — Live sample preview */}
        {src && (
          <button
            type="button"
            onClick={() => setShowSample((v) => !v)}
            className="mt-1 flex w-full items-center gap-1 text-[10px] text-emerald-700 hover:text-emerald-900"
            data-testid={`bulk-novel-voice-preview-toggle-${idx}`}
          >
            <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 font-semibold uppercase tracking-wider">
              Preview
            </span>
            {showSample ? 'Hide sample Claude will see' : 'Show sample Claude will see'}
          </button>
        )}
        {src && showSample && (
          <div
            className="mt-1 max-h-40 overflow-y-auto rounded-sm border border-emerald-200 bg-emerald-50/60 p-2 text-[10.5px] leading-relaxed text-neutral-800"
            data-testid={`bulk-novel-voice-preview-${idx}`}
          >
            {sampleLoading ? (
              <span className="text-neutral-500">Loading sample…</span>
            ) : (
              <pre className="whitespace-pre-wrap font-serif">{sampleText || 'Empty sample.'}</pre>
            )}
          </div>
        )}
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-[10.5px] text-neutral-600">
          Chapters
          <Input
            type="number" min={5} max={30}
            value={item.chapters_count}
            onChange={(e) => onChange({ chapters_count: Number(e.target.value) || 10 })}
            data-testid={`bulk-novel-chapters-${idx}`}
            disabled={!!selectedFramework}
            className="mt-0.5 h-8 text-[12px] disabled:opacity-60"
          />
        </label>
        <label className="text-[10.5px] text-neutral-600">
          Words / ch
          <Input
            type="number" min={800} max={6000} step={100}
            value={item.words_per_chapter}
            onChange={(e) => onChange({ words_per_chapter: Number(e.target.value) || 2500 })}
            data-testid={`bulk-novel-words-${idx}`}
            disabled={!!selectedFramework}
            className="mt-0.5 h-8 text-[12px] disabled:opacity-60"
          />
        </label>
      </div>
      <Textarea
        placeholder={selectedFramework ? 'Synopsis (optional — framework already sets beats)' : 'Seed synopsis — a paragraph or two Claude will honor.'}
        value={item.synopsis}
        onChange={(e) => onChange({ synopsis: e.target.value.slice(0, 6000) })}
        data-testid={`bulk-novel-synopsis-${idx}`}
        rows={3}
        className="resize-none text-[11.5px]"
      />
      <Textarea
        placeholder="Optional guidance ('lean dark & sensual', 'no first-person')"
        value={item.user_guidance}
        onChange={(e) => onChange({ user_guidance: e.target.value.slice(0, 2000) })}
        data-testid={`bulk-novel-guidance-${idx}`}
        rows={2}
        className="resize-none text-[11px]"
      />
      <div className="mt-auto text-[10px] text-neutral-500">
        ~{(item.chapters_count * item.words_per_chapter).toLocaleString()} words total
      </div>
    </div>
  );
}

const STATUS_META = {
  queued:            { icon: PauseCircle,    color: 'text-neutral-500', label: 'Queued' },
  drafting:          { icon: Loader2,        color: 'text-oxblood-600 animate-spin', label: 'Drafting' },
  completed:         { icon: CheckCircle2,   color: 'text-emerald-600', label: 'Completed' },
  failed:            { icon: AlertTriangle,  color: 'text-rose-600',    label: 'Failed' },
  cancelled:         { icon: Ban,            color: 'text-neutral-500', label: 'Cancelled' },
  blocked_by_budget: { icon: AlertTriangle,  color: 'text-amber-600',   label: 'Budget cap hit' },
};

function RunProgress({ run, onCancel, onReset }) {
  const anyRunning = (run.items || []).some((it) => !['completed', 'failed', 'cancelled', 'blocked_by_budget'].includes(it.status));
  const done  = run.totals?.chapters_done  || 0;
  const total = run.totals?.chapters_total || 0;
  const pct   = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="space-y-4" data-testid="bulk-novel-run-progress">
      <div>
        <div className="mb-1 flex items-center justify-between text-[11px] text-neutral-600">
          <span>Overall progress</span>
          <span className="font-mono-stat font-semibold text-oxblood-700">{done} / {total} chapters · {pct}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-200">
          <div className="h-full bg-oxblood-600 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-1 text-[10.5px] text-neutral-500">
          Total words drafted: <span className="font-mono-stat">{(run.totals?.words_total || 0).toLocaleString()}</span>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        {(run.items || []).map((it, idx) => {
          const meta = STATUS_META[it.status] || STATUS_META.queued;
          const Icon = meta.icon;
          const itemPct = it.chapters_total ? Math.round((it.chapters_done / it.chapters_total) * 100) : 0;
          return (
            <div key={idx} className="rounded-md border border-neutral-200 bg-white p-3 shadow-sm"
                 data-testid={`bulk-novel-run-item-${idx}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="line-clamp-1 text-[12.5px] font-semibold">{it.title}</div>
                  <div className="line-clamp-1 text-[10.5px] uppercase tracking-wider text-neutral-500">{it.genre || '—'}</div>
                </div>
                <div className={`ml-2 flex items-center gap-1 text-[10.5px] font-semibold ${meta.color}`}>
                  <Icon className="h-3 w-3" /> {meta.label}
                </div>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-neutral-200">
                <div className={
                  'h-full transition-all '
                  + (it.status === 'completed' ? 'bg-emerald-500'
                    : it.status === 'failed'   ? 'bg-rose-500'
                    : it.status === 'blocked_by_budget' ? 'bg-amber-500'
                    : 'bg-oxblood-500')
                } style={{ width: `${itemPct}%` }} />
              </div>
              <div className="mt-1 flex items-center justify-between text-[10.5px] text-neutral-500">
                <span>{it.chapters_done} / {it.chapters_total} chapters</span>
                {it.status === 'completed' && (
                  <a href={`/app/`} className="text-oxblood-600 hover:underline">Open project →</a>
                )}
              </div>
              {it.error && (
                <div className="mt-1 rounded-sm bg-rose-50 p-1.5 text-[10.5px] text-rose-700">
                  {it.error}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        {anyRunning && (
          <Button variant="ghost" onClick={onCancel} className="text-rose-600"
                  data-testid="bulk-novel-cancel-run">
            <Ban className="mr-1 h-3 w-3" /> Cancel remaining
          </Button>
        )}
        {!anyRunning && (
          <Button variant="outline" onClick={onReset} data-testid="bulk-novel-run-new">
            <Plus className="mr-1 h-3 w-3" /> Queue another batch
          </Button>
        )}
      </div>
    </div>
  );
}


// ── ZIP Import Panel ──────────────────────────────────────────────────────────

const ZIP_STAGE_LABELS = {
  import:       'Import',
  synopsis:     'Synopsis',
  world_builder:'World',
  plot:         'Plot',
  characters:   'Characters',
  continuity:   'Continuity',
  polish:       'Polish',
  export:       'Export',
  score:        'Score',
  completeness: 'Completeness',
};

const ZIP_STAGES = ['import','synopsis','world_builder','plot','characters','continuity','polish','export','score','completeness'];

function ZipImportPanel({
  run, uploading, dragging, expanded, fileInputRef,
  onDrop, onDragOver, onDragLeave, onFileChange, onCancel, onReset, onToggleExpand,
}) {
  if (!run) {
    return (
      <div className="flex flex-col gap-4" data-testid="zip-drop-area">
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={() => !uploading && fileInputRef.current?.click()}
          data-testid="zip-drop-zone"
          className={[
            'relative flex min-h-[220px] cursor-pointer flex-col items-center justify-center',
            'rounded-xl border-2 border-dashed p-8 transition-all duration-200',
            dragging
              ? 'border-oxblood-500 bg-oxblood-50 scale-[1.01]'
              : 'border-neutral-300 bg-neutral-50 hover:border-oxblood-400 hover:bg-white',
            uploading ? 'pointer-events-none opacity-60' : '',
          ].join(' ')}
        >
          {uploading ? (
            <>
              <Loader2 className="h-11 w-11 animate-spin text-oxblood-500" />
              <p className="mt-3 text-[13px] font-semibold text-neutral-700">Uploading &amp; extracting…</p>
            </>
          ) : (
            <>
              <FileArchive className={`h-12 w-12 ${dragging ? 'text-oxblood-600' : 'text-neutral-400'}`} />
              <p className="mt-3 text-[15px] font-semibold text-neutral-800">
                {dragging ? 'Drop to start pipeline' : 'Drop your .zip here'}
              </p>
              <p className="mt-0.5 text-[12px] text-neutral-500">or click to browse your files</p>
              <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1 text-[10.5px] text-neutral-400">
                <span>Up to 30 DOCX files</span>
                <span>·</span>
                <span>80 MB total</span>
                <span>·</span>
                <span>25 MB per file</span>
              </div>
            </>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".zip"
          className="hidden"
          onChange={onFileChange}
          data-testid="zip-file-input"
        />
        <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3.5 text-[11px] text-amber-900">
          <p className="font-semibold mb-1">Each novel runs through 9 AI stages in parallel batches:</p>
          <p className="text-neutral-600 leading-relaxed">
            Import &rarr; <span className="font-medium text-neutral-700">Synopsis + World Builder + Plot + Characters + Continuity</span> (concurrent)
            &rarr; Polish &rarr; Export &rarr; <span className="font-semibold text-emerald-700">Publishability Score</span>
          </p>
          <p className="mt-1.5 font-semibold text-emerald-700">
            Novels scoring 85%+ are flagged Ready to Publish.
          </p>
        </div>
      </div>
    );
  }

  const isDone = ['done', 'done_with_errors', 'cancelled'].includes(run.status);
  const done   = run.novels_done   || 0;
  const total  = run.total_novels  || 0;
  const pct    = total ? Math.round((done / total) * 100) : 0;
  const publishable = (run.items || []).filter((it) => it.publishability_priority);

  return (
    <div className="space-y-4" data-testid="zip-run-tracker">
      {/* Overall progress bar */}
      <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[12px] font-semibold text-neutral-700">
            Batch progress
            {run.status === 'cancelled' && (
              <span className="ml-2 rounded-full bg-neutral-300 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-neutral-700">
                Cancelled
              </span>
            )}
          </span>
          <span className="font-mono-stat text-[12px] font-semibold text-oxblood-700">
            {done} / {total} novels · {pct}%
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-neutral-200">
          <div
            className="h-full bg-oxblood-600 transition-all duration-700"
            style={{ width: `${pct}%` }}
          />
        </div>
        {publishable.length > 0 && (
          <div className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {publishable.length} novel{publishable.length > 1 ? 's' : ''} scored 85%+ — ready to publish
          </div>
        )}
      </div>

      {/* Per-novel cards */}
      <div className="space-y-2">
        {(run.items || []).map((item) => (
          <ZipNovelCard
            key={item.idx}
            item={item}
            expanded={!!expanded[item.idx]}
            onToggleExpand={() => onToggleExpand(item.idx)}
          />
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-1">
        {!isDone && (
          <Button
            variant="ghost"
            onClick={onCancel}
            className="text-[12px] text-rose-600 hover:bg-rose-50"
            data-testid="zip-cancel-btn"
          >
            <Ban className="mr-1 h-3 w-3" /> Cancel batch
          </Button>
        )}
        {isDone && (
          <Button
            variant="outline"
            onClick={onReset}
            className="text-[12px]"
            data-testid="zip-new-batch-btn"
          >
            <Plus className="mr-1 h-3 w-3" /> Import another batch
          </Button>
        )}
      </div>
    </div>
  );
}

function ZipNovelCard({ item, expanded, onToggleExpand }) {
  const stages    = item.stages || {};
  const score     = item.publishability_score;
  const isPriority = item.publishability_priority;
  const isDone    = item.status === 'done';
  const isFailed  = item.status === 'failed';

  const scoreColor =
    score >= 85 ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
    : score >= 60 ? 'border-amber-400 bg-amber-50 text-amber-700'
    : 'border-rose-300 bg-rose-50 text-rose-700';

  return (
    <div
      className={[
        'rounded-lg border p-3 transition-all',
        isPriority  ? 'border-emerald-400 bg-emerald-50/30 shadow-sm'
        : isFailed  ? 'border-rose-200 bg-rose-50/20'
        : 'border-neutral-200 bg-white',
      ].join(' ')}
      data-testid={`zip-novel-card-${item.idx}`}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-[13px] font-semibold text-neutral-900">
              {item.project_title || item.docx_filename}
            </span>
            {isPriority && (
              <span className="shrink-0 rounded-full bg-emerald-600 px-2 py-0.5 text-[8.5px] font-bold uppercase tracking-wider text-white">
                Ready to Publish
              </span>
            )}
          </div>
          {(item.chapter_count > 0 || item.word_count > 0) && (
            <p className="mt-0.5 text-[10.5px] text-neutral-500">
              {item.chapter_count > 0 && `${item.chapter_count} ch`}
              {item.chapter_count > 0 && item.word_count > 0 && ' · '}
              {item.word_count > 0 && `${item.word_count.toLocaleString()} words`}
            </p>
          )}
        </div>

        {/* Score circle (visible once score stage finishes) */}
        {score !== undefined && score !== null && (
          <div className={`shrink-0 flex h-11 w-11 flex-col items-center justify-center rounded-full border-2 ${scoreColor}`}
               data-testid={`zip-novel-score-${item.idx}`}>
            <span className="text-[12px] font-bold leading-none">{score}</span>
            <span className="text-[7.5px] font-semibold uppercase tracking-wide leading-none mt-0.5">Score</span>
          </div>
        )}

        <button
          onClick={onToggleExpand}
          className="shrink-0 rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
          data-testid={`zip-novel-expand-${item.idx}`}
        >
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      </div>

      {/* Compact stage dots when collapsed */}
      {!expanded && (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          {ZIP_STAGES.map((s) => (
            <ZipStageDot key={s} stage={s} status={(stages[s] || {}).status || 'pending'} />
          ))}
        </div>
      )}

      {/* Expanded stage list */}
      {expanded && (
        <div className="mt-3 space-y-1.5 border-t border-neutral-100 pt-3">
          {ZIP_STAGES.map((s) => (
            <ZipStageRow key={s} stage={s} info={stages[s] || {}} />
          ))}

          {/* Publishability detail when scored */}
          {score !== undefined && score !== null && (
            <div className={`mt-2 rounded-md p-2.5 text-[11px] ${isPriority ? 'bg-emerald-50 border border-emerald-200' : 'bg-neutral-50 border border-neutral-200'}`}
                 data-testid={`zip-novel-pub-detail-${item.idx}`}>
              <p className={`font-semibold mb-1 ${isPriority ? 'text-emerald-800' : 'text-neutral-700'}`}>
                Publishability: {score}/100{isPriority ? ' — Ready to Publish' : ''}
              </p>
              {item.publishability_reasoning && (
                <p className="text-neutral-600 italic mb-1.5">"{item.publishability_reasoning}"</p>
              )}
              {(item.publishability_strengths || []).length > 0 && (
                <div>
                  <span className="font-semibold text-emerald-700">Strengths: </span>
                  {(item.publishability_strengths).join(' · ')}
                </div>
              )}
              {(item.publishability_improvements || []).length > 0 && (
                <div className="mt-0.5">
                  <span className="font-semibold text-amber-700">To improve: </span>
                  {(item.publishability_improvements).join(' · ')}
                </div>
              )}
            </div>
          )}

          {/* Completeness report when Stage 10 finishes */}
          {item.completeness_checklist && item.completeness_checklist.length > 0 && (
            <div className={`mt-2 rounded-md border p-2.5 text-[11px] ${
              item.completeness_pass
                ? 'border-emerald-200 bg-emerald-50/40'
                : 'border-amber-200 bg-amber-50/40'
            }`} data-testid={`zip-novel-completeness-${item.idx}`}>
              <div className="flex items-center justify-between mb-2">
                <p className={`font-semibold ${item.completeness_pass ? 'text-emerald-800' : 'text-amber-800'}`}>
                  Craft Standards — {item.completeness_pass ? 'All 10 checks passed' : `${item.completeness_deviations || 0} deviation${(item.completeness_deviations || 0) !== 1 ? 's' : ''} flagged`}
                </p>
                {(item.completeness_rewrites || 0) > 0 && (
                  <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-700">
                    {item.completeness_rewrites} rewrite{item.completeness_rewrites !== 1 ? 's' : ''} applied
                  </span>
                )}
              </div>
              {item.completeness_summary && (
                <p className="italic text-neutral-600 mb-2">"{item.completeness_summary}"</p>
              )}
              <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                {item.completeness_checklist.map((check) => (
                  <div key={check.id} className="flex items-start gap-1.5">
                    <span className={`mt-0.5 shrink-0 text-[10px] font-bold ${
                      check.status === 'pass'      ? 'text-emerald-600'
                      : check.status === 'deviation' ? 'text-amber-600'
                      : 'text-rose-600'
                    }`}>
                      {check.status === 'pass' ? '✓' : check.status === 'deviation' ? '!' : '✗'}
                    </span>
                    <div className="min-w-0">
                      <span className="font-medium text-neutral-700">{check.label}</span>
                      {check.status !== 'pass' && check.detail && (
                        <p className="text-[10px] text-neutral-500 leading-tight mt-0.5 truncate" title={check.detail}>
                          {check.detail}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {item.error && (
            <div className="mt-1 rounded-sm bg-rose-50 p-2 text-[10.5px] text-rose-700">
              {item.error}
            </div>
          )}
          {item.project_id && isDone && (
            <a
              href="/app/"
              className="mt-2 inline-flex items-center gap-1 text-[10.5px] text-oxblood-600 hover:underline"
              data-testid={`zip-novel-open-${item.idx}`}
            >
              <ExternalLink className="h-3 w-3" /> Open in editor
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function ZipStageDot({ stage, status }) {
  const cls = {
    pending: 'bg-neutral-200',
    running: 'bg-amber-400 animate-pulse',
    done:    'bg-emerald-500',
    error:   'bg-rose-500',
    skipped: 'bg-neutral-200 opacity-40',
  }[status] || 'bg-neutral-200';
  return (
    <div
      title={`${ZIP_STAGE_LABELS[stage] || stage}: ${status}`}
      className={`h-2 w-2 rounded-full ${cls}`}
    />
  );
}

function ZipStageRow({ stage, info }) {
  const status = info.status || 'pending';
  const iconMap = {
    pending: PauseCircle,
    running: Loader2,
    done:    Check,
    error:   AlertTriangle,
    skipped: Minus,
  };
  const colorMap = {
    pending: 'text-neutral-400',
    running: 'text-amber-600',
    done:    'text-emerald-600',
    error:   'text-rose-600',
    skipped: 'text-neutral-300',
  };
  const Icon  = iconMap[status]  || PauseCircle;
  const color = colorMap[status] || 'text-neutral-400';
  return (
    <div className="flex items-center gap-2">
      <Icon className={`h-3 w-3 shrink-0 ${color} ${status === 'running' ? 'animate-spin' : ''}`} />
      <span className="w-24 shrink-0 text-[11px] text-neutral-700">{ZIP_STAGE_LABELS[stage] || stage}</span>
      <span className={`text-[10.5px] font-medium capitalize ${color}`}>{status}</span>
      {info.detail && (
        <span className="ml-1 truncate text-[10px] text-neutral-500">{info.detail}</span>
      )}
    </div>
  );
}
